import { Injectable, inject } from '@angular/core';

import { MessageService } from 'primeng/api';

type ToastOptions = {
  life?: number;
  severity?:
    | 'success'
    | 'info'
    | 'warn'
    | 'error'
    | 'help'
    | 'primary'
    | 'secondary'
    | 'contrast';
  link?: string;
  linkText?: string;
  queryParams?: Record<string, any>;
};

@Injectable()
export class CustomToastService {
  private messageService = inject(MessageService);

  /**
   * This show toast shows the customToast that it is in
   * @link src\app\shared\components\custom-toast\custom-toast.component.ts.
   *
   * By default it is a success toast, so it shows in green and with a check icon.
   * You have to set the message in the function.
   * In order to work, you need to import the component into the component page you want it to appear.
   *
   * @example
   * ```ts
   * import { CustomToast } from '@components/custom-toast/custom-toast.component';
   *
   * \@Component({
   *   selector: 'company-overview-page',
   *   standalone: true,
   *   imports: [
   *     // ... other imports
   *     CustomToast
   *   ],
   *   template: `
   *     <div>...</div>
   *     <custom-toast/>
   *   `,
   *   // ... other properties
   * })
   * export class CompanyOverviewPage { ... }
   * ```
   * @param message The main message to be displayed in the toast.
   * @param options (Optional) An object to customize the toast's appearance, such as severity, life, and summary.
   */
  showToast(message: string, options?: ToastOptions) {
    const { link, linkText, queryParams, ...rest } = options ?? {};

    const defaultOptions = {
      key: 'confirm',
      life: 2000,
      severity: 'success',
      summary: message,
      data: link ? { link, linkText, queryParams } : undefined,
    };

    const finalOptions = { ...defaultOptions, ...rest };

    this.messageService.clear(finalOptions.key);
    this.messageService.add(finalOptions);
  }
}
